import { DiamondRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Menu,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../api/hooks/useSmartAndRegularAssets";
import { assetKeys, entityKeys } from "../../../../api/queryKeys";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import { Toast } from "../../../../components/Toast";
import { AssetClass, PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import AssetCreationOrEditModal from "../../assets/components/AssetCreationOrEditModal";

// TODO - To be deleted after entities V2 feature is complete.
// https://linear.app/breeze-security/issue/DEV-2575/remove-files-and-code-of-old-entities-page

export default function AddToAssetButton({
  entity,
  diamondStyle,
  sx = {},
  children = null,
  tooltipTitle = "Set containing assets",
}) {
  const colors = useColors();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedAssetIDs, setSelectedAssetIDs] = useState([]);
  const [assetCreationModalIsOpen, setAssetCreationModalIsOpen] =
    useState(false);

  const {
    refreshEntitiesOverview,
    updateAssetsContainingTheEntity,
    trackEvent,
  } = useAPI();
  const queryClient = useQueryClient();

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_ASSET
  );

  const {
    regularAndActiveSmartAsset: allAssets,
    isLoadingAll: assetsFetchIsLoading,
  } = useSmartAndRegularAssets(false);

  useEffect(() => {
    if (allAssets && assetsFetchIsLoading === false) {
      setSelectedAssetIDs(getIDsOfAssetsContainingTheEntity(allAssets));
    }
  }, [allAssets]);

  const { mutate: updateTheAssetsContainingTheEntity } = useMutation({
    mutationFn: async () => {
      const response = await updateAssetsContainingTheEntity(
        entity.entity_id,
        selectedAssetIDs
      );
      return response;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.all,
      });
      Toast.success("Successfully updated critical assets for entity");
      setMenuAnchorEl(null);
      await refreshEntitiesOverview();
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesOverview(),
      });
    },
    onError: () => {
      Toast.error("Error updating the assets containing this entity");
    },
  });

  const getIDsOfAssetsContainingTheEntity = (allAssets) => {
    if (!allAssets) return [];
    const containingAssets = allAssets.filter((asset) =>
      asset.entity_ids.includes(entity?.entity_id)
    );
    return containingAssets.map((asset) => asset.asset_id);
  };

  const handleDiamondClick = (event) => {
    trackEvent(
      `User clicked the diamond of entity ${entity?.entity_id} to edit its containing assets`
    );
    setMenuAnchorEl(event.currentTarget);
  };

  const onCheckboxChange = (assetID) => {
    if (selectedAssetIDs.includes(assetID)) {
      setSelectedAssetIDs(selectedAssetIDs.filter((id) => id !== assetID));
    } else {
      setSelectedAssetIDs([...selectedAssetIDs, assetID]);
    }
  };

  const isAnyChangeInSelectedAssets = () => {
    const assetsWithTheEntity = getIDsOfAssetsContainingTheEntity(allAssets);
    return !(
      new Set(selectedAssetIDs).size === new Set(assetsWithTheEntity).size &&
      selectedAssetIDs.every((id) => assetsWithTheEntity.includes(id))
    );
  };

  const onMenuClose = () => {
    if (isAnyChangeInSelectedAssets()) {
      Toast.info("Updating containing assets...");
      trackEvent(
        `User added/removed entity ${entity?.entity_id} from an asset`
      ).then();
      updateTheAssetsContainingTheEntity();
    }
    setMenuAnchorEl(null);
  };

  const handleCloseAssetCreationModal = () => {
    queryClient.invalidateQueries({
      queryKey: assetKeys.all,
    });
    setAssetCreationModalIsOpen(false);
    trackEvent(
      `User closed the assets edit menu of entity ${entity?.entity_id}`
    );
  };

  // We ignore active smart assets that don't contain the entity
  const relevantAssets = allAssets
    ? allAssets.filter(
        (asset) =>
          asset?.cls === AssetClass.REGULAR ||
          (asset?.cls === AssetClass.SMART &&
            selectedAssetIDs.includes(asset.asset_id))
      )
    : [];

  return (
    <>
      <AssetCreationOrEditModal
        isOpen={assetCreationModalIsOpen}
        onClose={handleCloseAssetCreationModal}
      />
      <CustomToolTipWithPermission
        title={tooltipTitle}
        permission={hasPermission}
        placement="top-start"
      >
        <Button
          sx={{ pt: children ? "0px" : "10px", ...sx }}
          onClick={hasPermission ? handleDiamondClick : null}
        >
          {children ? (
            children
          ) : (
            <DiamondRounded
              sx={{
                color:
                  getIDsOfAssetsContainingTheEntity(allAssets).length > 0
                    ? colors.primary_main
                    : colors.grey_dark,

                ...diamondStyle,
              }}
            />
          )}
        </Button>
      </CustomToolTipWithPermission>
      <Menu
        id="simple-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={onMenuClose}
        sx={{
          ".MuiPaper-root": {
            borderRadius: 2,
            boxShadow: "0px 2px 5px 1px " + colors.grey_500,
          },
        }}
      >
        <Box mx={1}>
          <FormGroup>
            {relevantAssets.map((asset) => (
              <CustomToolTip
                title={
                  <Typography variant="caption">
                    {asset.name} is a Smart Critical Asset and is managed
                    automatically by Breeze. You can turn it on or off on the
                    Critical Assets page
                  </Typography>
                }
                disableHoverListener={asset?.cls === AssetClass.REGULAR}
                key={asset.asset_id}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAssetIDs.includes(asset.asset_id)}
                      onChange={() => onCheckboxChange(asset.asset_id)}
                      sx={{
                        "& svg": { fontSize: 15 },
                        "&.MuiCheckbox-root": {
                          color: colors.primary_main, // Change the color of the unchecked state
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="caption"
                      sx={{ color: colors.common_black }}
                    >
                      {asset.name}
                    </Typography>
                  }
                  disabled={asset?.cls === AssetClass.SMART}
                />
              </CustomToolTip>
            ))}
          </FormGroup>
          <Divider />
          <Box
            onClick={() => setAssetCreationModalIsOpen(true)}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              cursor: "pointer",
              paddingTop: 1,
            }}
          >
            <Typography variant="body2" color={colors.primary_main}>
              +
            </Typography>
            <Typography variant="caption" sx={{ mx: 1 }}>
              Add new...
            </Typography>
          </Box>
        </Box>
      </Menu>
    </>
  );
}
